/**
 * useCircularList – custom hook to manage the circular list data and functions.
 */
import DeviceConfig from '@devices/config';
import Direction from '@shared/types/enums/direction';
import Focus from '@shared/types/enums/focus';
import useKeyboard from '@shared/components/utils/hooks/useKeyboard';
import { focusSelector } from '@shared/store/slices/focus.slice';
import { useAppSelector } from '@shared/store/hooks';
import {
  Fragment,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

/**
 * Constants
 *
 * POST_WRAP_BLOCK_UNTIL: The time in milliseconds to block key presses after a wrap. (60 milliseconds)
 */
const POST_WRAP_BLOCK_UNTIL = 60;

/**
 * Props for the CircularList component.
 */
export interface CircularListProps<T> {
  direction?: Direction;
  duration: number;
  focus?: Focus | number;
  initialIndex?: number;
  items: T[];
  offset: number;
  onEnter?: (item: T) => void;
  onChange?: (index: number) => void;
  onKeyDown?: () => void;
  onKeyUp?: () => void;
  renderItem: (item: T, index: number) => ReactNode;
}

/**
 * Custom hook to manage the circular list data and functions.
 * @param props - Props for the CircularList component.
 * @returns {Object} An object containing the circular list data and functions.
 */
export default function useCircularList<T>({
  direction = Direction.HORIZONTAL,
  duration: originalDuration,
  focus,
  initialIndex = 0,
  items,
  onEnter,
  onChange,
  onKeyDown,
  onKeyUp,
  renderItem,
}: CircularListProps<T>) {
  // ------------------------------------------------------------
  // Initialization
  // ------------------------------------------------------------

  // Selectors
  const { focus: currentFocus } = useAppSelector(focusSelector);

  const animationsDisabled = DeviceConfig.capabilities.disableAnimations;
  const length = items?.length ?? 0;
  const circularLength = animationsDisabled ? length : length + 2;

  // State: when animations disabled use logical index (0..length-1); otherwise internal index (0..length+1)
  const [duration, setDuration] = useState(originalDuration);
  const [index, setIndex] = useState(() => {
    const safe = Math.min(
      Math.max(0, initialIndex),
      Math.max(0, (items?.length ?? 0) - 1)
    );
    return animationsDisabled ? safe : safe + 1;
  });

  // Variables
  const focused =
    focus === currentFocus ||
    (Array.isArray(currentFocus) &&
      ((currentFocus as (number | string)[]).includes(focus as number) ||
        (currentFocus as (number | string)[]).includes(String(focus))));

  // Refs
  const animatingRef = useRef<boolean>(false); // Whether the list is currently animating.
  const blockPressUntilRef = useRef<number>(0); // Prevents multiple key presses from moving the index multiple times.
  const circularLengthRef = useRef(circularLength);
  const focusedRef = useRef(focused); // Whether the list is currently focused.
  const indexRef = useRef<number>(index); // The current index.
  const itemsRef = useRef(items);
  const lastReportedRef = useRef<number>(initialIndex); // The last reported logical index.
  const lastMoveTimeRef = useRef<number>(0); // The last time the index was moved.
  const lengthRef = useRef(length);
  circularLengthRef.current = circularLength;
  focusedRef.current = focused;
  indexRef.current = index;
  itemsRef.current = items;
  lengthRef.current = length;

  // ------------------------------------------------------------
  // Callbacks
  // ------------------------------------------------------------

  // Converts a logical index to an internal index (no-op when animations disabled).
  const logicalToInternal = useCallback(
    (logical: number) =>
      animationsDisabled ? logical : Math.min(logical + 1, length),
    [animationsDisabled, length]
  );

  // Converts an internal index to a logical index (identity when animations disabled).
  const internalToLogical = useCallback(
    (internal: number) =>
      animationsDisabled
        ? internal
        : internal === 0
        ? lengthRef.current - 1
        : internal === circularLengthRef.current - 1
        ? 0
        : internal - 1,
    [animationsDisabled]
  );

  // Reports the current logical index when the index changes.
  const reportChange = useCallback(
    (logicalIndex: number) => {
      if (lastReportedRef.current === logicalIndex) return;
      lastReportedRef.current = logicalIndex;
      onChange?.(logicalIndex);
    },
    [onChange]
  );

  // Moves the index by a given delta. When animations disabled, index is logical and wraps 0..length-1.
  const moveByOffset = useCallback(
    (offset: number) => {
      if (!lengthRef.current) return;
      const len = lengthRef.current;
      setIndex((prev) => {
        if (animationsDisabled) {
          return (prev + offset + len) % len;
        }
        let next = prev + offset;
        if (next < 0) next = circularLengthRef.current - 1;
        if (next > circularLengthRef.current - 1)
          next = circularLengthRef.current - 1;
        return next;
      });
    },
    [animationsDisabled]
  );

  // Tries to move the index by a given key and pressures.
  const navigate = useCallback(
    (key: string, pressures: number) => {
      if (animatingRef.current) return;
      if (!lengthRef.current) return;
      const now = Date.now();
      if (now < blockPressUntilRef.current) return;
      const firstPress = pressures <= 1;
      const throttleMs = originalDuration * 1000;
      const throttled =
        !firstPress &&
        throttleMs > 0 &&
        now - lastMoveTimeRef.current < throttleMs;
      if (throttled) return;
      const animationsDisabled = DeviceConfig.capabilities.disableAnimations;
      if (!animationsDisabled) animatingRef.current = true;
      lastMoveTimeRef.current = now;

      if (direction === Direction.HORIZONTAL) {
        if (key === 'RIGHT') moveByOffset(1);
        else if (key === 'LEFT') moveByOffset(-1);
      } else {
        if (key === 'DOWN') moveByOffset(1);
        else if (key === 'UP') moveByOffset(-1);
      }
    },
    [direction, originalDuration, moveByOffset]
  );

  // Handles the transition end event (no-op when animations disabled; no duplicates to snap).
  const onTransitionEnd = useCallback(() => {
    animatingRef.current = false;
    if (animationsDisabled || !lengthRef.current) return;
    // Right wrap: was at right duplicate (length+1) -> snap to real first item (internal index: 1)
    if (index === circularLengthRef.current - 1) {
      setDuration(0);
      setIndex(1);
      lastReportedRef.current = 0;
      blockPressUntilRef.current = Date.now() + POST_WRAP_BLOCK_UNTIL;
      setTimeout(() => {
        setDuration(originalDuration);
        lastMoveTimeRef.current = 0;
      }, 50);
      return;
    }
    // Left wrap: was at left duplicate (0) -> snap to real last (internal length)
    if (index === 0) {
      setDuration(0);
      setIndex(lengthRef.current);
      lastReportedRef.current = lengthRef.current - 1;
      blockPressUntilRef.current = Date.now() + POST_WRAP_BLOCK_UNTIL;
      setTimeout(() => {
        setDuration(originalDuration);
        lastMoveTimeRef.current = 0;
      }, 50);
    }
  }, [animationsDisabled, index, originalDuration, onChange]);

  // ------------------------------------------------------------
  // Effects
  // ------------------------------------------------------------

  // When animations are disabled, transitionEnd never fires; run wrap-snap and clear logic after index updates.
  useEffect(() => {
    if (!DeviceConfig.capabilities.disableAnimations) return;
    onTransitionEnd();
  }, [index, onTransitionEnd]);

  // Syncs the index from the parent (e.g. timer / auto-advance).
  useEffect(() => {
    if (!length) return;
    const logical = Math.min(Math.max(0, initialIndex), length - 1);
    if (animationsDisabled) {
      setIndex(logical);
      return;
    }
    const nextInternal = logicalToInternal(logical);
    setIndex((prev) => {
      if (prev === 0 || prev === circularLengthRef.current - 1) return prev;
      if (
        prev !== nextInternal &&
        prev === lengthRef.current &&
        nextInternal === 1
      )
        return circularLengthRef.current - 1;
      if (
        prev !== nextInternal &&
        prev === 1 &&
        nextInternal === lengthRef.current
      )
        return 0;
      return prev !== nextInternal ? nextInternal : prev;
    });
  }, [animationsDisabled, length, initialIndex, logicalToInternal]);

  // Report current logical index when index changes; skip when no items to avoid reporting -1
  useEffect(() => {
    if (!lengthRef.current) return;
    const logical = internalToLogical(index);
    if (logical < 0 || logical >= lengthRef.current) return;
    reportChange(logical);
  }, [index, internalToLogical, reportChange]);

  // ------------------------------------------------------------
  // Keyboard
  // ------------------------------------------------------------

  // Checks if a key is a navigation key.
  const isNavKey = useCallback(
    (key: string) => {
      if (direction === Direction.HORIZONTAL)
        return key === 'LEFT' || key === 'RIGHT';
      return key === 'UP' || key === 'DOWN';
    },
    [direction]
  );

  // Handles the keyboard events.
  useKeyboard({
    focus,
    keyDownHandler: (key, _longPress, pressures) => {
      if (!focusedRef.current || !lengthRef.current) return;
      if (key === 'SELECT') {
        const logical = internalToLogical(indexRef.current);
        onEnter?.(itemsRef.current[logical]);
        return;
      }
      if (!isNavKey(key)) return;
      onKeyDown?.();
      navigate(key, pressures ?? 0);
    },
    keyUpHandler: (key) => {
      if (isNavKey(key)) onKeyUp?.();
    },
  });

  // ------------------------------------------------------------
  // Memoization
  // ------------------------------------------------------------

  // When animations disabled: no duplicates. When enabled: duplicate first/last for seamless wrap animation.
  const circularItems = useMemo(() => {
    if (!length) return [];
    return animationsDisabled ? items : [items[length - 1], ...items, items[0]];
  }, [animationsDisabled, items, length]);

  // Returns the children of the CircularList component.
  const children = useMemo(() => {
    return circularItems.map((item, i) => (
      <Fragment key={i}>{renderItem(item, internalToLogical(i))}</Fragment>
    ));
  }, [circularItems, internalToLogical, renderItem]);

  return {
    children,
    duration,
    index,
    onTransitionEnd,
  };
}
